// Chemin: C:\gestion-copro\src\types\suiviBailleur.types.ts
// Session 217 : Création — module Suivi Bailleur VIGIE
// Modèle Firestore : suiviBailleur/{userId}/locataires/{locataireId}
//                                           /paiements/{paiementId}

import { Timestamp } from 'firebase/firestore';

// ============================================
// LOCATAIRE
// ============================================

export interface Locataire {
  id: string;
  userId: string; // owner — isolé par userId, jamais par coproprieteId

  // Identité
  nom: string;
  prenom: string;
  email?: string;
  telephone?: string;

  // Bien loué
  adresseBien: string;        // Adresse complète du logement loué
  descriptionLot?: string;    // Ex : "Appartement T3, 3ème étage"

  // Bail
  dateEntree: Timestamp;
  dateSortie?: Timestamp;     // Absent = bail en cours
  loyerMensuel: number;       // Loyer charges comprises ou hors charges
  chargesProvision: number;   // Provision mensuelle pour charges (0 si incluses)
  depotGarantie?: number;

  // Statut
  actif: boolean;             // false = ancien locataire archivé

  // Métadonnées
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CreateLocataireData {
  nom: string;
  prenom: string;
  email?: string;
  telephone?: string;
  adresseBien: string;
  descriptionLot?: string;
  dateEntree: Date;
  dateSortie?: Date;
  loyerMensuel: number;
  chargesProvision: number;
  depotGarantie?: number;
}

// ============================================
// PAIEMENT
// ============================================

export type TypePaiement = 'loyer' | 'charges' | 'regularisation' | 'depot_garantie' | 'autre';
export type StatutPaiement = 'attendu' | 'recu' | 'partiel' | 'retard' | 'impaye';

export const TYPE_PAIEMENT_LABELS: Record<TypePaiement, string> = {
  loyer: 'Loyer',
  charges: 'Charges',
  regularisation: 'Régularisation annuelle',
  depot_garantie: 'Dépôt de garantie',
  autre: 'Autre',
};

export const STATUT_PAIEMENT_LABELS: Record<StatutPaiement, string> = {
  attendu: 'Attendu',
  recu: 'Reçu',
  partiel: 'Partiel',
  retard: 'En retard',
  impaye: 'Impayé',
};

export const STATUT_PAIEMENT_COLORS: Record<StatutPaiement, string> = {
  attendu: 'bg-blue-100 text-blue-800',
  recu: 'bg-green-100 text-green-800',
  partiel: 'bg-amber-100 text-amber-800',
  retard: 'bg-orange-100 text-orange-800',
  impaye: 'bg-red-100 text-red-800',
};

export interface Paiement {
  id: string;
  locataireId: string;
  userId: string;

  type: TypePaiement;
  statut: StatutPaiement;

  // Montants
  montantAttendu: number;
  montantRecu: number;       // 0 si impayé
  ecart: number;             // montantAttendu - montantRecu (positif = manque)

  // Période
  moisConcerne: string;      // Format "2025-01" — mois auquel se rapporte le paiement
  dateLimitePaiement?: Timestamp;
  datePaiementEffectif?: Timestamp;

  // Annotations
  note?: string;

  // Métadonnées
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export interface CreatePaiementData {
  type: TypePaiement;
  montantAttendu: number;
  montantRecu: number;
  moisConcerne: string;
  dateLimitePaiement?: Date;
  datePaiementEffectif?: Date;
  note?: string;
}

// ============================================
// RÉSUMÉ BAILLEUR (pour l'affichage dashboard)
// ============================================

export interface ResumeLocataire {
  locataire: Locataire;
  totalAttenduAnnee: number;
  totalRecuAnnee: number;
  solde: number;              // positif = dette locataire, négatif = trop-perçu bailleur
  dernierStatut: StatutPaiement | null;
  nbPaiementsEnRetard: number;
  nbPaiementsImpaye: number;
}

// ============================================
// HELPERS
// ============================================

/**
 * Calcule le statut d'un paiement à partir des montants et de la date
 */
export function calculerStatutPaiement(
  montantAttendu: number,
  montantRecu: number,
  dateLimitePaiement?: Date
): StatutPaiement {
  if (montantRecu >= montantAttendu) return 'recu';
  if (montantRecu > 0) return 'partiel';

  // Pas reçu du tout
  if (dateLimitePaiement && new Date() > dateLimitePaiement) {
    // Plus de 30 jours de retard → impayé
    const joursRetard = Math.floor(
      (new Date().getTime() - dateLimitePaiement.getTime()) / (1000 * 60 * 60 * 24)
    );
    return joursRetard > 30 ? 'impaye' : 'retard';
  }

  return 'attendu';
}

/**
 * Formate un mois "2025-01" en "Janvier 2025"
 */
export function formatMoisConcerne(moisConcerne: string): string {
  const [year, month] = moisConcerne.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
}

/**
 * Retourne le mois courant au format "YYYY-MM"
 */
export function getMoisCourant(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}
